/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
// @ts-ignore
import '@splidejs/splide/css';
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { getTestProps } from 'lib/testing/utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import useDictionary from 'lib/hooks/useDictionary';
import useIsEditing from 'lib/hooks/useIsEditing';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import { useSetIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import SvgIcon from 'helpers/SvgIcon/SvgIcon';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const getExpertProfileUrl = (url)=>{
    if (url.includes('/our-people/')) {
        const pattern = /\/our-people\/([a-z])\/(.+)/;
        const match = url.match(pattern);
        if (match && match[2]) {
            return `/our-people/${match[2]}`;
        }
    }
    return url;
};
const mapGraphQLProfile = (profile, index)=>{
    const firstName = profile.firstname?.jsonValue?.value;
    const lastName = profile.lastname?.jsonValue?.value;
    const href = profile.url?.url ? getExpertProfileUrl(profile.url.url) : undefined;
    return {
        key: href || `related-profile-${index}`,
        name: firstName && lastName ? {
            value: `${firstName} ${lastName}`
        } : firstName ? {
            value: firstName
        } : lastName ? {
            value: lastName
        } : undefined,
        jobtitle: profile.jobtitle?.jsonValue,
        picture: profile.picture?.jsonValue,
        href
    };
};
const resolveRelatedProfiles = (props)=>{
    const propsItems = props?.fields?.data?.item?.relatedProfiles?.targetItems ?? [];
    return propsItems.map(mapGraphQLProfile);
};
const RelatedProfiles = (props)=>{
    const { getDictionaryValue } = useDictionary();
    const isEditing = useIsEditing();
    const setIsDataAvailable = useSetIsDataAvailable();
    const mobileSplideRef = useRef(null);
    const desktopSplideRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(null);
    const relatedProfiles = resolveRelatedProfiles(props);
    const { RenderingIdentifier } = props?.params || {};
    const profileCtaText = getDictionaryValue('RelatedExpertCommentsCTAText') || 'Find out more';
    const hasProfiles = relatedProfiles.length > 0;
    const hasNoProfiles = !hasProfiles;
    useEffect(()=>{
        const available = !isEditing && hasProfiles;
        setIsDataAvailable(available);
    }, [
        hasProfiles,
        isEditing,
        setIsDataAvailable
    ]);
    const { container, sliderWrapper, splideTrackWrapper, splideSlide, card, imageSection, imageWrapper, contentSection, textBlock, contentBottom, nameText, jobTitleText, separator, ctaWrapper, ctaLink, arrowPrev, arrowNext, pagination, arrowIcon, gridWrapper, gridItem } = TAILWIND_VARIANTS();
    if (isEditing && hasNoProfiles) {
        return /*#__PURE__*/ _jsx("section", {
            "data-component": "authorable/shared/content/relatedprofiles",
            ...editingHydrationProps(isEditing),
            id: RenderingIdentifier,
            ...getTestProps(`component-related-profiles-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsx(EditingHelpText, {
                priority: "warning",
                children: getDictionaryValue('RelatedProfilesHelpText') || 'No related profiles are available for this page. Related profiles are populated from page data in the Content Editor.'
            })
        });
    }
    if (hasNoProfiles) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    const slideCount = relatedProfiles.length;
    const isSingleSlide = slideCount === 1;
    const showGridLayout = slideCount <= 3;
    const showDesktopArrows = slideCount >= 4;
    const isPrevDisabled = currentIndex === 0;
    const isNextDisabled = endIndex !== null && currentIndex >= endIndex;
    const onDesktopSplideMove = (instance)=>{
        setCurrentIndex(instance.index);
        setEndIndex(instance.Components.Controller.getEnd());
    };
    const mobileSplideOptions = {
        arrows: false,
        gap: '24px',
        pagination: !isSingleSlide,
        perMove: 1,
        perPage: 1,
        omitEnd: true,
        rewind: false,
        rewindByDrag: false,
        speed: 500,
        type: 'slide',
        width: '100%',
        drag: !isSingleSlide,
        padding: {
            right: 0,
            left: 0
        }
    };
    const desktopSplideOptions = {
        arrows: false,
        gap: '24px',
        pagination: !isSingleSlide,
        perMove: 1,
        perPage: 3,
        omitEnd: true,
        rewind: false,
        rewindByDrag: false,
        speed: 500,
        type: 'slide',
        width: '100%',
        drag: !isSingleSlide,
        padding: isSingleSlide ? {
            right: 0,
            left: 0
        } : {
            right: '30%'
        },
        breakpoints: {
            1024: {
                perPage: 2,
                padding: isSingleSlide ? {
                    right: 0,
                    left: 0
                } : {
                    right: '30%'
                }
            }
        }
    };
    const renderProfileCard = (profile)=>{
        const profileLink = profile.href ? {
            value: {
                href: profile.href,
                text: profileCtaText
            }
        } : undefined;
        return /*#__PURE__*/ _jsxs("article", {
            className: card(),
            ...getTestProps(`related-profile-card-${profile.key}`),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: imageSection(),
                    children: profile.picture?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                        field: profile.picture,
                        className: imageWrapper(),
                        ...getTestProps(`related-profile-image-${profile.key}`)
                    }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                        field: {
                            value: {
                                src: '/assets/profile_placeholder.png',
                                alt: 'No image'
                            }
                        },
                        className: imageWrapper(),
                        ...getTestProps(`related-profile-image-placeholder`)
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: contentSection(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: textBlock(),
                            children: [
                                /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    className: nameText(),
                                    field: profile.name,
                                    tag: "div",
                                    ...getTestProps(`related-profile-name-${profile.key}`)
                                }),
                                /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    className: jobTitleText(),
                                    field: profile.jobtitle,
                                    tag: "div",
                                    ...getTestProps(`related-profile-role-${profile.key}`)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: contentBottom(),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: separator()
                                }),
                                profileLink?.value?.href && /*#__PURE__*/ _jsx("div", {
                                    className: ctaWrapper(),
                                    children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaComponentClass: "component-card-item-button-1-color",
                                        className: ctaLink(),
                                        field: profileLink,
                                        ctaIcon: "arrow-right",
                                        ctaIconSize: "s",
                                        ctaStyle: getCtaStyle({
                                            ctaVariant: 'tertiary'
                                        }, 'tertiary'),
                                        "aria-label": profileLink?.value?.text ? `${profileLink?.value?.text} for ${profile.name?.value || 'profile'}` : `Find out more about ${profile.name?.value || 'profile'}`,
                                        ...getTestProps(`related-profile-link-${profile.key}`)
                                    })
                                })
                            ]
                        })
                    ]
                })
            ]
        });
    };
    const renderSplideSlides = ()=>relatedProfiles.map((profile)=>/*#__PURE__*/ _jsx(SplideSlide, {
                className: splideSlide(),
                children: renderProfileCard(profile)
            }, profile.key));
    const renderMobileSlider = ()=>/*#__PURE__*/ _jsxs(Splide, {
            ref: (ref)=>{
                if (ref?.splide) {
                    mobileSplideRef.current = ref.splide;
                }
            },
            hasTrack: false,
            options: mobileSplideOptions,
            children: [
                /*#__PURE__*/ _jsx(SplideTrack, {
                    className: splideTrackWrapper({
                        isSingleSlide
                    }),
                    children: renderSplideSlides()
                }),
                !isSingleSlide && /*#__PURE__*/ _jsx("ul", {
                    className: `splide__pagination ${pagination()}`
                })
            ]
        });
    const renderDesktopSlider = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                showDesktopArrows && /*#__PURE__*/ _jsx(ButtonWrapper, {
                    ctaComponentClass: "default",
                    ctaVariant: "tertiary",
                    onClick: ()=>desktopSplideRef.current?.go('<'),
                    className: arrowPrev({
                        isArrowDisabled: isPrevDisabled
                    }),
                    disabled: isPrevDisabled,
                    "aria-label": "Previous slide",
                    children: /*#__PURE__*/ _jsx(SvgIcon, {
                        icon: "prev-arrow",
                        className: arrowIcon(),
                        viewBox: "0 0 18 21"
                    })
                }),
                /*#__PURE__*/ _jsxs(Splide, {
                    ref: (ref)=>{
                        if (ref?.splide) {
                            desktopSplideRef.current = ref.splide;
                        }
                    },
                    hasTrack: false,
                    onMounted: onDesktopSplideMove,
                    onMoved: onDesktopSplideMove,
                    onUpdated: onDesktopSplideMove,
                    options: desktopSplideOptions,
                    children: [
                        /*#__PURE__*/ _jsx(SplideTrack, {
                            className: splideTrackWrapper({
                                isSingleSlide
                            }),
                            children: renderSplideSlides()
                        }),
                        !isSingleSlide && /*#__PURE__*/ _jsx("ul", {
                            className: `splide__pagination ${pagination()}`
                        })
                    ]
                }),
                showDesktopArrows && /*#__PURE__*/ _jsx(ButtonWrapper, {
                    ctaComponentClass: "default",
                    ctaVariant: "tertiary",
                    onClick: ()=>desktopSplideRef.current?.go('>'),
                    className: arrowNext({
                        isArrowDisabled: isNextDisabled
                    }),
                    disabled: isNextDisabled,
                    "aria-label": "Next slide",
                    children: /*#__PURE__*/ _jsx(SvgIcon, {
                        icon: "prev-arrow",
                        className: arrowIcon(),
                        size: "s",
                        viewBox: "0 0 18 21"
                    })
                })
            ]
        });
    return /*#__PURE__*/ _jsx("section", {
        "data-component": "authorable/shared/content/relatedprofiles",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-related-profiles-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsx("div", {
            className: container(),
            children: /*#__PURE__*/ _jsxs("div", {
                className: sliderWrapper(),
                children: [
                    showGridLayout && /*#__PURE__*/ _jsx("div", {
                        className: gridWrapper(),
                        "aria-label": "Related profiles",
                        children: relatedProfiles.map((profile)=>/*#__PURE__*/ _jsx("div", {
                                className: gridItem(),
                                children: renderProfileCard(profile)
                            }, profile.key))
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "md:hidden",
                        children: renderMobileSlider()
                    }),
                    !showGridLayout && /*#__PURE__*/ _jsx("div", {
                        className: "hidden md:block",
                        children: renderDesktopSlider()
                    })
                ]
            })
        })
    });
};
export default withStandardComponentWrapper(RelatedProfiles, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        container: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24'
        ],
        sliderWrapper: [
            'w-full',
            'relative'
        ],
        card: [
            'flex',
            'flex-col',
            'h-full',
            'w-full',
            'overflow-hidden',
            'rounded-general-border-radius-card',
            'shadow-color-black-alpha-10',
            'shadow-[0_0_8px_0]'
        ],
        imageSection: [
            'overflow-hidden',
            'h-[289px]',
            'shrink-0',
            'grow'
        ],
        imageWrapper: [
            'w-full',
            'h-full',
            'object-cover',
            'aspect-[4/3]'
        ],
        contentSection: [
            'flex',
            'flex-col',
            'flex-1',
            'h-full',
            'gap-spacing-spacing-24',
            'p-spacing-spacing-40',
            'bg-component-featureCard-surface',
            'items-start'
        ],
        textBlock: [
            'flex',
            'flex-col',
            'flex-1',
            'w-full',
            'text-left'
        ],
        contentBottom: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'mt-auto',
            'w-full'
        ],
        nameText: [
            'text-component-section-heading',
            'font-semibold',
            'text-white'
        ],
        jobTitleText: [
            'text-component-section-body',
            'font-typography-font-family-body',
            'text-white',
            'text-typography-font-size-body-medium',
            'font-normal'
        ],
        separator: [
            'w-full',
            'h-[2px]',
            'bg-white'
        ],
        ctaWrapper: [
            'flex',
            'p-spacing-spacing-2',
            'items-center',
            'justify-between',
            'self-stretch'
        ],
        ctaLink: [
            '!text-white',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-typography-line-height-xxsmall',
            'flex',
            'items-center',
            'justify-between',
            'w-full',
            'transition-opacity',
            'duration-200',
            '[&:hover_svg]:!text-white'
        ],
        splideTrackWrapper: [
            '[&_.splide__list]:items-stretch',
            '!overflow-visible'
        ],
        splideSlide: [
            '!h-auto',
            'flex'
        ],
        arrowPrev: [
            'absolute',
            'top-1/2',
            '-translate-y-1/2',
            'z-10',
            'border-general-border-width-pagination-button',
            'border-solid',
            'border-component-carousel-utility-bar-button-border',
            'rounded-general-border-radius-tag',
            'text-white',
            '!p-spacing-spacing-12',
            'hover:opacity-90',
            'transition-opacity',
            'hidden',
            'lg:block',
            'lg:left-[-2vw]',
            'xl:!left-[-4.5vw]',
            'bg-component-carousel-utility-bar-button-bg'
        ],
        arrowNext: [
            'absolute',
            'top-1/2',
            '-translate-y-1/2',
            'lg:right-[-2vw]',
            'right-0',
            'z-10',
            'border-general-border-width-pagination-button',
            'border-solid',
            'border-component-carousel-utility-bar-button-border',
            'rounded-general-border-radius-tag',
            'text-white',
            '!p-spacing-spacing-12',
            'rotate-180',
            'hover:opacity-90',
            'transition-opacity',
            'hidden',
            'lg:block',
            'bg-component-carousel-utility-bar-button-bg'
        ],
        pagination: [
            '!mt-[40px]',
            'p-0',
            'pt-component-section-container-gutter',
            'flex',
            'justify-center',
            '!relative',
            '[&>li]:p-[6px]',
            '[&>li>button]:w-spacing-spacing-12',
            '[&>li>button]:h-spacing-spacing-12',
            '[&>li>button]:bg-transparent',
            '[&>li>button]:!border',
            '[&>li>button]:!border-color-core-color-1-Navy',
            '[&>li>button]:!border-solid',
            '[&>li>button]:rounded-full',
            '[&>li>button]:m-0',
            '[&>li>button.is-active]:bg-color-core-color-1-Navy',
            '[&>li>button.is-active]:transform-none',
            '[&>li>button.is-active]:opacity-100',
            '[&>li>button.is-active]:w-spacing-spacing-40'
        ],
        arrowIcon: [
            '!h-[18px]',
            '!w-[18px]'
        ],
        gridWrapper: [
            'hidden',
            'md:flex',
            'w-full',
            'gap-spacing-spacing-24',
            'items-stretch',
            'justify-center'
        ],
        gridItem: [
            'shrink-0',
            'flex',
            'w-[calc((100%-48px)/3)]'
        ]
    },
    variants: {
        isSingleSlide: {
            true: {
                splideTrackWrapper: [
                    'w-full'
                ]
            },
            false: {
                splideTrackWrapper: [
                    'w-full',
                    'lg:w-screen',
                    'lg:mr-[calc(50%-50vw)]'
                ]
            }
        },
        isArrowDisabled: {
            true: {
                arrowPrev: [
                    'opacity-50',
                    'cursor-not-allowed'
                ],
                arrowNext: [
                    'opacity-50',
                    'cursor-not-allowed'
                ]
            }
        }
    },
    defaultVariants: {
        isSingleSlide: false,
        isArrowDisabled: false
    }
});
